import { concat, Hash, keccak256, toBytes, toHex } from "viem";
import {
  entryPoint06Address,
  getUserOperationHash,
} from "viem/account-abstraction";
import { DEFAULT_CHAIN_ID } from ".";
import capsule from "./capsule";
import Capsule, {
  hexStringToBase64,
  SuccessfulSignatureRes,
} from "@usecapsule/web-sdk";
import { ToLogsSmartAccountReturnType } from "@/pages/org/toLogsSmartAccount";

export async function customSignMessage(
  capsule: Capsule,
  message: string
): Promise<Hash> {
  const prefix = "\x19Ethereum Signed Message:\n32";
  const prefixedMessage = concat([toBytes(prefix), toBytes(message)]);
  const hashedMessage = keccak256(prefixedMessage);

  console.log(capsule.wallets!, "capsule.wallets!");
  console.log(
    Object.values(capsule.wallets!),
    "  Object.values(capsule.wallets!)"
  );

  const res = await capsule.signMessage(
    Object.values(capsule.wallets!)[0]!.id,
    hexStringToBase64(hashedMessage)
  );

  let signature = (res as SuccessfulSignatureRes).signature;

  // Fix the v value of the signature
  const lastByte = parseInt(signature.slice(-2), 16);
  if (lastByte < 27) {
    const adjustedV = (lastByte + 27).toString(16).padStart(2, "0");
    signature = signature.slice(0, -2) + adjustedV;
  }

  return `0x${signature}`;
}

export async function viemSignUserOp(userOp: BundlerUserOperation) {
  const message = getUserOperationHash({
    chainId: DEFAULT_CHAIN_ID,
    entryPointAddress: entryPoint06Address,
    entryPointVersion: "0.6",
    userOperation: {
      ...userOp,
      nonce: BigInt(parseInt(userOp.nonce, 16)),
      callGasLimit: BigInt(parseInt(userOp.callGasLimit, 16)),
      verificationGasLimit: BigInt(parseInt(userOp.verificationGasLimit, 16)),
      preVerificationGas: BigInt(parseInt(userOp.preVerificationGas, 16)),
      maxFeePerGas: BigInt(parseInt(userOp.maxFeePerGas, 16)),
      maxPriorityFeePerGas: BigInt(parseInt(userOp.maxPriorityFeePerGas, 16)),
    },
  });

  const signature = await customSignMessage(capsule, message);
  return signature;
}

export interface BundlerUserOperation {
  sender: `0x${string}`;
  nonce: `0x${string}`;
  initCode: `0x${string}`;
  callData: `0x${string}`;
  callGasLimit: `0x${string}`;
  verificationGasLimit: `0x${string}`;
  preVerificationGas: `0x${string}`;
  maxFeePerGas: `0x${string}`;
  maxPriorityFeePerGas: `0x${string}`;
  paymasterAndData: `0x${string}`;
  signature: `0x${string}`;
}

class Bundler {
  private factoryArgs: {
    factory: `0x${string}` | undefined;
    factoryData: `0x${string}` | undefined;
  } = {
    factory: undefined,
    factoryData: undefined,
  };

  private isDeployed: boolean = false;

  constructor(
    private bundlerUrl: string,
    private entryPointAddress: string,
    private chainId: number,
    private smartAccount: any
  ) {}

  async initialize() {
    this.factoryArgs = await this.smartAccount.getFactoryArgs();
    this.isDeployed = await this.smartAccount.isDeployed();
  }

  async sendTransaction(callData: `0x${string}`) {
    const nonce = await this.smartAccount.getNonce();
    if (!this.isDeployed) {
      this.isDeployed = await this.smartAccount.isDeployed();
    }

    const initCode = (
      this.isDeployed
        ? "0x"
        : concat([
            this.factoryArgs.factory!,
            this.factoryArgs.factoryData!,
          ]).toString()
    ) as `0x${string}`;

    // 2. 构建基础 UserOperation
    const userOp: BundlerUserOperation = {
      sender: this.smartAccount.address,
      nonce: toHex(nonce),
      initCode,
      callData,
      callGasLimit: "0x0",
      verificationGasLimit: "0x0",
      preVerificationGas: "0x0",
      maxFeePerGas: "0x0",
      maxPriorityFeePerGas: "0x0",
      paymasterAndData: "0x0",
      signature:
        "0xfffffffffffffffffffffffffffffff0000000000000000000000000000000007aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa1c",
    };

    // 3. 获取 gas 价格并设置
    const gasPrices = await this.getUserOperationGasPrice();
    userOp.maxFeePerGas = gasPrices.standard.maxFeePerGas as `0x${string}`;
    userOp.maxPriorityFeePerGas = gasPrices.standard
      .maxPriorityFeePerGas as `0x${string}`;

    // 4. 获取 paymaster 数据
    const paymasterStubData = await this.getPaymasterStuData(userOp);
    userOp.paymasterAndData = paymasterStubData.paymasterAndData;

    // 5. 估算 gas
    const gasEstimates = await this.estimateUserOpGas(userOp);
    userOp.callGasLimit = toHex(BigInt(gasEstimates.callGasLimit));
    userOp.verificationGasLimit = toHex(
      BigInt(Math.floor(Number(gasEstimates.verificationGasLimit) * 2))
    );
    userOp.preVerificationGas = toHex(
      BigInt(Math.floor(Number(gasEstimates.preVerificationGas) * 2))
    );

    // 6. 获取最终 paymaster 数据
    const paymasterData = await this.getPaymasterData(userOp);
    userOp.paymasterAndData = paymasterData.paymasterAndData;
    // 7. 签名
    userOp.signature = await viemSignUserOp(userOp);

    console.log(userOp, "userOp");
    // 8. 发送交易
    const userOpHash = await this.sendUserOperation(userOp);
    console.log("userOpHash", userOpHash);

    return {
      userOpHash,
      wait: async () => {
        let status;
        console.time("wait for status");
        do {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          status = await this.getUserOperationStatus(userOpHash);
        } while (!status);
        console.timeEnd("wait for status");
        return status;
      },
    };
  }

  async sendUserOperation(userOp: BundlerUserOperation): Promise<string> {
    const response = await fetch(this.bundlerUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "eth_sendUserOperation",
        params: [userOp, this.entryPointAddress],
      }),
    });

    const result = await response.json();
    if (result.error) {
      throw new Error(`Bundler 错误: ${result.error.message}`);
    }

    return result.result; // 返回 userOpHash
  }

  // 获取 UserOperation 的状态
  async getUserOperationStatus(userOpHash: string): Promise<any> {
    const response = await fetch(this.bundlerUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "eth_getUserOperationByHash",
        params: [userOpHash],
      }),
    });

    return (await response.json()).result;
  }

  // 获取支持的 entry points
  async getSupportedEntryPoints(): Promise<string[]> {
    const response = await fetch(this.bundlerUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "eth_supportedEntryPoints",
        params: [],
      }),
    });

    return (await response.json()).result;
  }

  async getPaymasterStuData(userOp: BundlerUserOperation): Promise<any> {
    const response = await fetch(this.bundlerUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "pm_getPaymasterStubData",
        params: [userOp, this.entryPointAddress, toHex(this.chainId)],
      }),
    });

    const result = await response.json();
    if (result.error) {
      throw new Error(`获取paymaster数据失败: ${result.error.message}`);
    }

    return result.result;
  }

  async estimateUserOpGas(userOp: BundlerUserOperation): Promise<{
    callGasLimit: string;
    verificationGasLimit: string;
    preVerificationGas: string;
  }> {
    const response = await fetch(this.bundlerUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "eth_estimateUserOperationGas",
        params: [userOp, this.entryPointAddress],
      }),
    });

    const result = await response.json();
    if (result.error) {
      throw new Error(`估算gas失败: ${result.error.message}`);
    }

    return {
      callGasLimit: result.result.callGasLimit,
      verificationGasLimit: result.result.verificationGasLimit,
      preVerificationGas: result.result.preVerificationGas,
    };
  }

  async getUserOperationGasPrice(): Promise<{
    slow: { maxFeePerGas: string; maxPriorityFeePerGas: string };
    standard: { maxFeePerGas: string; maxPriorityFeePerGas: string };
    fast: { maxFeePerGas: string; maxPriorityFeePerGas: string };
  }> {
    const response = await fetch(this.bundlerUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "pimlico_getUserOperationGasPrice",
        params: [],
      }),
    });

    const result = await response.json();
    if (result.error) {
      throw new Error(`获取gas价格失败: ${result.error.message}`);
    }

    return result.result;
  }

  async getPaymasterData(userOp: Partial<BundlerUserOperation>) {
    const response = await fetch(this.bundlerUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        method: "pm_getPaymasterData",
        params: [userOp, this.entryPointAddress, toHex(this.chainId)],
        id: 1,
      }),
    });

    const result = await response.json();
    if (result.error) {
      throw new Error(`获取paymaster数据失败: ${result.error.message}`);
    }

    return result.result;
  }

  async getTokenQuotes(tokens: string[]): Promise<{
    quotes: Array<{
      paymaster: string;
      token: string;
      postOpGas: string;
      exchangeRate: string;
      exchangeRateNativeToUsd: string;
    }>;
  }> {
    const response = await fetch(this.bundlerUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        method: "pimlico_getTokenQuotes",
        params: [
          {
            tokens: tokens,
          },
          this.entryPointAddress,
          toHex(this.chainId),
        ],
        id: 1,
      }),
    });

    const result = await response.json();
    if (result.error) {
      throw new Error(`获取代币报价失败: ${result.error.message}`);
    }

    return result.result;
  }
}

export async function createBundler(
  smartAccount: ToLogsSmartAccountReturnType
) {
  const bundler = new Bundler(
    `https://api.pimlico.io/v2/${DEFAULT_CHAIN_ID}/rpc?apikey=pim_AGauh3jiy1y2myffubsGKg`,
    entryPoint06Address,
    DEFAULT_CHAIN_ID,
    smartAccount
  );
  await bundler.initialize();
  return bundler;
}
